// Payments/CreatePaymentLinkForm.js

import React, { useState } from 'react';
import axiosInstance from "../../../utils/axiosInstance";
import {
  Box, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Grid, Card, CardContent, Switch, FormControlLabel, Chip, Alert, Divider,
  IconButton, Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Paper, Stack, alpha, useTheme, InputAdornment, Stepper, Step,
  StepLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PreviewIcon from '@mui/icons-material/Preview';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentIcon from '@mui/icons-material/Payment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LinkIcon from '@mui/icons-material/Link';
import TokenIcon from '@mui/icons-material/Token';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const FONT = "'Plus Jakarta Sans', sans-serif";

const STEPS = ['Details', 'Amounts', 'Payment Methods', 'Settings'];

const STEP_ICONS = [
  <DescriptionIcon fontSize="small" />,
  <AttachMoneyIcon fontSize="small" />,
  <PaymentIcon fontSize="small" />,
  <SettingsIcon fontSize="small" />
];

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 2 }
};

const sectionCardSx = (theme) => ({
  elevation: 0,
  borderRadius: 3,
  border: `1px solid ${theme.palette.divider}`,
  p: 3
});

const CreatePaymentLinkForm = ({ onSuccess, permission, cryptoConfig }) => {
  const theme = useTheme();

  const [activeStep, setActiveStep] = useState(0);
  const [stepErrors, setStepErrors] = useState({});

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    disclaimer: '',
    payment_type: 'others',
    recurring_type: 'onetime',
    suggested_amounts: ['10.00', '25.00', '50.00', '100.00'],
    purposes: ['General'],
    enable_for_chatbot: false,
    custom_amount_enabled: true,
    collect_customer_info: true,
    payment_methods: {
      stripe: true,
      paypal: false,
      coinbase: false
    },
    crypto_token: 'USDC',
    payment_frequencies: {
      onetime: true,
      recurring: false
    },
    processing_fee_handling: 'absorbed',
    processing_fee_percentage: 2.9,
    fixed_fee: 0.30,
    thank_you_message: 'Thank you for your payment!',
    redirect_url: '',
    expires_at: '',
    is_active: true
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSuggestedAmountChange = (index, value) => {
    const newAmounts = [...formData.suggested_amounts];
    newAmounts[index] = value;
    setFormData(prev => ({
      ...prev,
      suggested_amounts: newAmounts
    }));
  };

  const addSuggestedAmount = () => {
    setFormData(prev => ({
      ...prev,
      suggested_amounts: [...prev.suggested_amounts, '']
    }));
  };

  const handlePurposeChange = (index, value) => {
    const newPurposes = [...formData.purposes];
    newPurposes[index] = value;
    setFormData(prev => ({
      ...prev,
      purposes: newPurposes
    }));
  };

  const addPurpose = () => {
    setFormData(prev => ({
      ...prev,
      purposes: [...prev.purposes, '']
    }));
  };

  const removePurpose = (index) => {
    setFormData(prev => ({
      ...prev,
      purposes: prev.purposes.filter((_, i) => i !== index)
    }));
  };

  const removeSuggestedAmount = (index) => {
    setFormData(prev => ({
      ...prev,
      suggested_amounts: prev.suggested_amounts.filter((_, i) => i !== index)
    }));
  };

  const calculateProcessingFee = (amount) => {
    const baseAmount = parseFloat(amount || 0);
    const percentageFee = (baseAmount * formData.processing_fee_percentage) / 100;
    const totalFee = percentageFee + formData.fixed_fee;
    return totalFee.toFixed(2);
  };

  // --- Step validation ---
  const validateStep = (step) => {
    const errors = {};
    switch (step) {
      case 0:
        if (!formData.title.trim()) errors.title = 'Payment title is required';
        if (!formData.payment_type) errors.payment_type = 'Payment type is required';
        if (!formData.payment_frequencies.onetime && !formData.payment_frequencies.recurring) {
          errors.frequencies = 'Select at least one payment frequency';
        }
        if (formData.purposes.length === 0 || formData.purposes.every(p => !p.trim())) {
          errors.purposes = 'At least one purpose is required';
        }
        break;
      case 1:
        if (formData.suggested_amounts.length === 0) {
          errors.amounts = 'At least one suggested amount is required';
        }
        const invalidAmounts = formData.suggested_amounts.filter(
          a => a !== '' && (isNaN(parseFloat(a)) || parseFloat(a) < 0)
        );
        if (invalidAmounts.length > 0) {
          errors.amounts = 'All amounts must be valid positive numbers';
        }
        break;
      case 2:
        if (!formData.payment_methods.stripe && !formData.payment_methods.paypal && !formData.payment_methods.coinbase) {
          errors.methods = 'Select at least one payment method';
        }
        break;
      case 3:
        break;
      default:
        break;
    }
    return errors;
  };

  const handleNext = () => {
    const errors = validateStep(activeStep);
    if (Object.keys(errors).length > 0) {
      setStepErrors(errors);
      return;
    }
    setStepErrors({});
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setStepErrors({});
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const errors = validateStep(activeStep);
    if (Object.keys(errors).length > 0) {
      setStepErrors(errors);
      return;
    }
    setIsSubmitting(true);
    setError('');

    try {
      const payload = { ...formData };
      if (!payload.payment_methods.coinbase) {
        delete payload.crypto_token;
      }

      const response = await axiosInstance.post(
        `/payments/create-advanced-payment-link`,
        payload
      );

      setGeneratedLink(`${window.location.origin}/pay/${response.data.token}`);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create payment link');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink).then(() => {
      alert('Payment link copied to clipboard!');
    });
  };

  const generateQRCode = () => {
    alert('QR Code generation would be implemented here');
  };

  const handleReset = () => {
    setGeneratedLink('');
    setActiveStep(0);
    setError('');
    setStepErrors({});
    setFormData({
      title: '',
      description: '',
      disclaimer: '',
      payment_type: 'others',
      recurring_type: 'onetime',
      suggested_amounts: ['10.00', '25.00', '50.00', '100.00'],
      purposes: ['General'],
      enable_for_chatbot: false,
      custom_amount_enabled: true,
      collect_customer_info: true,
      payment_methods: {
        stripe: true,
        paypal: false,
        coinbase: false
      },
      crypto_token: 'USDC',
      payment_frequencies: {
        onetime: true,
        recurring: false
      },
      processing_fee_handling: 'absorbed',
      processing_fee_percentage: 2.9,
      fixed_fee: 0.30,
      thank_you_message: 'Thank you for your payment!',
      redirect_url: '',
      expires_at: '',
      is_active: true
    });
  };

  // =========================================================================
  //  SUCCESS STATE
  // =========================================================================
  if (generatedLink) {
    const token = generatedLink.split('/pay/')[1] || '';
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', mt: 2 }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            borderLeft: `5px solid ${theme.palette.success.main}`,
            overflow: 'visible'
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 32 }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontFamily: FONT, color: 'success.dark' }}
              >
                Payment Link Created
              </Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: FONT }}>
              Your payment link is ready. Share it with customers or embed it on your website.
            </Typography>

            {/* Link URL */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.success.main, 0.04),
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <LinkIcon sx={{ fontSize: 18, color: 'success.main' }} />
                <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: FONT, color: 'text.secondary' }}>
                  PAYMENT LINK
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  fullWidth
                  value={generatedLink}
                  InputProps={{ readOnly: true }}
                  size="small"
                  sx={{
                    ...fieldSx,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontFamily: 'monospace',
                      fontSize: '0.85rem'
                    }
                  }}
                />
                <Tooltip title="Copy Link">
                  <IconButton
                    onClick={handleCopyLink}
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: 'success.main',
                      '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.2) }
                    }}
                    size="small"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Generate QR Code">
                  <IconButton
                    onClick={generateQRCode}
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: 'success.main',
                      '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.2) }
                    }}
                    size="small"
                  >
                    <QrCodeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Paper>

            {/* Token */}
            {token && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.grey[500], 0.04),
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <TokenIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: FONT, color: 'text.secondary' }}>
                    TOKEN
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', wordBreak: 'break-all', color: 'text.primary' }}
                >
                  {token}
                </Typography>
              </Paper>
            )}

            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
              fullWidth
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontFamily: FONT,
                py: 1.2
              }}
            >
              Create Another Payment Link
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // =========================================================================
  //  STEP CONTENT RENDERERS
  // =========================================================================

  const renderStepDetails = () => (
    <Card elevation={0} sx={sectionCardSx(theme)}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <DescriptionIcon sx={{ color: theme.palette.primary.main }} fontSize="small" />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: FONT }}>
          Payment Details
        </Typography>
      </Stack>

      <Grid container spacing={2.5}>
        {/* Title */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Payment Title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="e.g., Mosque Donation, Course Fee, Membership"
            size="small"
            sx={fieldSx}
            error={!!stepErrors.title}
            helperText={stepErrors.title}
          />
        </Grid>

        {/* Payment Type */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required size="small" error={!!stepErrors.payment_type} sx={fieldSx}>
            <InputLabel>Payment Type</InputLabel>
            <Select
              name="payment_type"
              value={formData.payment_type}
              label="Payment Type"
              onChange={handleInputChange}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="donation">Donation</MenuItem>
              <MenuItem value="signup">Signup / Registration</MenuItem>
              <MenuItem value="subscription">Subscription</MenuItem>
              <MenuItem value="others">Others</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Payment Frequencies */}
        <Grid item xs={12} sm={6}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.04),
              border: stepErrors.frequencies
                ? `1px solid ${theme.palette.error.main}`
                : `1px solid ${alpha(theme.palette.info.main, 0.2)}`
            }}
          >
            <Typography variant="caption" sx={{ mb: 1, fontWeight: 700, display: 'block', fontFamily: FONT }} color="text.primary">
              Payment Frequencies
            </Typography>
            <Stack spacing={0.5}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={formData.payment_frequencies.onetime}
                    onChange={handleInputChange}
                    name="payment_frequencies.onetime"
                  />
                }
                label={<Typography variant="body2" sx={{ fontFamily: FONT }}>One-time Payment</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={formData.payment_frequencies.recurring}
                    onChange={handleInputChange}
                    name="payment_frequencies.recurring"
                  />
                }
                label={<Typography variant="body2" sx={{ fontFamily: FONT }}>Recurring Payment</Typography>}
              />
            </Stack>
            {stepErrors.frequencies && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {stepErrors.frequencies}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Describe what this payment is for..."
            size="small"
            sx={fieldSx}
          />
        </Grid>

        {/* Disclaimer */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Disclaimer (Optional)"
            name="disclaimer"
            value={formData.disclaimer}
            onChange={handleInputChange}
            placeholder="Any terms, conditions, or disclaimers..."
            size="small"
            sx={fieldSx}
          />
        </Grid>

        {/* Purposes */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" sx={{ mt: 2, mb: 0.5, fontWeight: 700, fontFamily: FONT }} color="text.primary">
            Payment Purposes (Categories)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block', fontFamily: FONT }}>
            Define purposes/categories for this payment. Customers will select one when paying.
          </Typography>
          {stepErrors.purposes && (
            <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
              {stepErrors.purposes}
            </Typography>
          )}

          <Grid container spacing={2}>
            {formData.purposes.map((purpose, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth
                    label={`Purpose ${index + 1}`}
                    value={purpose}
                    onChange={(e) => handlePurposeChange(index, e.target.value)}
                    placeholder="e.g., Donation, Zakat, Course Fee"
                    size="small"
                    sx={fieldSx}
                  />
                  <IconButton
                    onClick={() => removePurpose(index)}
                    disabled={formData.purposes.length <= 1}
                    size="small"
                    sx={{
                      color: 'error.main',
                      '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Grid>
            ))}

            {formData.purposes.length < 10 && (
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addPurpose}
                  fullWidth
                  size="small"
                  sx={{
                    height: 40,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontFamily: FONT,
                    borderStyle: 'dashed'
                  }}
                >
                  Add Purpose
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Recurring Interval (shown if recurring is on) */}
        {formData.payment_frequencies.recurring && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Recurring Interval</InputLabel>
              <Select
                name="recurring_type"
                value={formData.recurring_type}
                label="Recurring Interval"
                onChange={handleInputChange}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="onetime">One-time</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>
    </Card>
  );

  const renderStepAmounts = () => (
    <Card elevation={0} sx={sectionCardSx(theme)}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <AttachMoneyIcon sx={{ color: theme.palette.primary.main }} fontSize="small" />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: FONT }}>
          Amount Configuration
        </Typography>
      </Stack>

      <Typography variant="body2" sx={{ mb: 2, fontWeight: 700, fontFamily: FONT }} color="text.primary">
        Suggested Amounts
      </Typography>

      {stepErrors.amounts && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontFamily: FONT }}>{stepErrors.amounts}</Typography>
        </Alert>
      )}

      <Grid container spacing={2}>
        {formData.suggested_amounts.map((amount, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                fullWidth
                type="number"
                label={`Amount ${index + 1}`}
                value={amount}
                onChange={(e) => handleSuggestedAmountChange(index, e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ step: "0.01", min: "0" }}
                size="small"
                sx={fieldSx}
              />
              <IconButton
                onClick={() => removeSuggestedAmount(index)}
                disabled={formData.suggested_amounts.length <= 1}
                size="small"
                sx={{
                  color: 'error.main',
                  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Grid>
        ))}

        {formData.suggested_amounts.length < 6 && (
          <Grid item xs={6} sm={3}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addSuggestedAmount}
              fullWidth
              size="small"
              sx={{
                height: 40,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontFamily: FONT,
                borderStyle: 'dashed'
              }}
            >
              Add
            </Button>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 3 }} />

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={formData.custom_amount_enabled}
            onChange={handleInputChange}
            name="custom_amount_enabled"
          />
        }
        label={
          <Typography variant="body2" sx={{ fontFamily: FONT }}>
            Allow custom amount input
          </Typography>
        }
      />

      {/* Quick preview chips */}
      {formData.suggested_amounts.some(a => a && parseFloat(a) > 0) && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: FONT, color: 'text.secondary', mb: 1, display: 'block' }}>
            PREVIEW
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {formData.suggested_amounts
              .filter(a => a && parseFloat(a) > 0)
              .map((a, i) => (
                <Chip
                  key={i}
                  label={`$${parseFloat(a).toFixed(2)}`}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    fontFamily: FONT,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                    border: 'none'
                  }}
                />
              ))}
            {formData.custom_amount_enabled && (
              <Chip
                label="Custom"
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  fontFamily: FONT,
                  borderStyle: 'dashed'
                }}
              />
            )}
          </Stack>
        </Box>
      )}
    </Card>
  );

  const renderStepPaymentMethods = () => (
    <Card elevation={0} sx={sectionCardSx(theme)}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <PaymentIcon sx={{ color: theme.palette.primary.main }} fontSize="small" />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: FONT }}>
          Payment Methods & Processing Fees
        </Typography>
      </Stack>

      {stepErrors.methods && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontFamily: FONT }}>{stepErrors.methods}</Typography>
        </Alert>
      )}

      {/* Accepted Methods */}
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
        }}
      >
        <Typography variant="caption" sx={{ mb: 1.5, fontWeight: 700, display: 'block', fontFamily: FONT, letterSpacing: 0.5 }} color="text.secondary">
          ACCEPTED PAYMENT METHODS
        </Typography>
        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={formData.payment_methods.stripe}
                onChange={handleInputChange}
                name="payment_methods.stripe"
              />
            }
            label={<Typography variant="body2" sx={{ fontFamily: FONT }}>Stripe (Credit/Debit Cards)</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={formData.payment_methods.paypal}
                onChange={handleInputChange}
                name="payment_methods.paypal"
              />
            }
            label={<Typography variant="body2" sx={{ fontFamily: FONT }}>PayPal</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={formData.payment_methods.coinbase}
                onChange={handleInputChange}
                name="payment_methods.coinbase"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontFamily: FONT }}>
                Coinbase (Crypto -- USDC, ETH, BTC, SOL)
              </Typography>
            }
          />
        </Stack>
      </Paper>

      {/* Crypto Token selector */}
      {formData.payment_methods.coinbase && (
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.04),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
          }}
        >
          <Typography variant="caption" sx={{ mb: 1.5, fontWeight: 700, display: 'block', fontFamily: FONT }} color="text.primary">
            Preferred Crypto Token
          </Typography>
          <FormControl fullWidth size="small" sx={fieldSx}>
            <InputLabel>Crypto Token</InputLabel>
            <Select
              name="crypto_token"
              value={formData.crypto_token}
              label="Crypto Token"
              onChange={handleInputChange}
              sx={{ borderRadius: 2 }}
            >
              {['USDC', 'ETH', 'BTC', 'SOL'].map((token) => (
                <MenuItem key={token} value={token}>{token}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      <Divider sx={{ my: 2.5 }} />

      {/* Processing Fee Handling */}
      <Typography variant="body2" sx={{ mb: 2, fontWeight: 700, fontFamily: FONT }} color="text.primary">
        Processing Fee Handling
      </Typography>

      <FormControl fullWidth sx={{ mb: 2.5 }} size="small">
        <InputLabel>Fee Handling</InputLabel>
        <Select
          name="processing_fee_handling"
          value={formData.processing_fee_handling}
          label="Fee Handling"
          onChange={handleInputChange}
          sx={{ borderRadius: 2 }}
        >
          <MenuItem value="absorbed">Organization absorbs fees</MenuItem>
          <MenuItem value="added">Always add fees to customer</MenuItem>
          <MenuItem value="user_choice">Let customer choose</MenuItem>
        </Select>
      </FormControl>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            label="Processing Fee %"
            name="processing_fee_percentage"
            value={formData.processing_fee_percentage}
            onChange={handleInputChange}
            inputProps={{ step: "0.1", min: "0", max: "10" }}
            size="small"
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            label="Fixed Fee ($)"
            name="fixed_fee"
            value={formData.fixed_fee}
            onChange={handleInputChange}
            inputProps={{ step: "0.01", min: "0" }}
            size="small"
            sx={fieldSx}
          />
        </Grid>
      </Grid>

      {/* Fee example alert */}
      {formData.suggested_amounts[0] && (
        <Alert
          severity="info"
          sx={{ mt: 2.5, borderRadius: 2 }}
          icon={<InfoOutlinedIcon fontSize="small" />}
        >
          <Typography variant="body2" sx={{ fontFamily: FONT }}>
            Example: ${formData.suggested_amounts[0]} + ${calculateProcessingFee(formData.suggested_amounts[0])} fee = ${(parseFloat(formData.suggested_amounts[0] || 0) + parseFloat(calculateProcessingFee(formData.suggested_amounts[0]))).toFixed(2)} total
          </Typography>
        </Alert>
      )}

      {/* Fee breakdown */}
      {formData.suggested_amounts[0] && parseFloat(formData.suggested_amounts[0]) > 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            mt: 2.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.success.main, 0.04),
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
          }}
        >
          <Typography variant="caption" sx={{ mb: 1.5, fontWeight: 700, display: 'block', fontFamily: FONT, letterSpacing: 0.5 }} color="text.secondary">
            FEE BREAKDOWN (BASED ON FIRST SUGGESTED AMOUNT)
          </Typography>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONT }}>Gross Amount</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontFamily: FONT }}>
                ${parseFloat(formData.suggested_amounts[0]).toFixed(2)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONT }}>
                Platform Fee ({formData.processing_fee_percentage}%)
              </Typography>
              <Typography variant="body2" fontWeight={600} color="error.main" sx={{ fontFamily: FONT }}>
                -${calculateProcessingFee(formData.suggested_amounts[0])}
              </Typography>
            </Stack>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ fontFamily: FONT }}>
                You Receive
              </Typography>
              <Typography variant="body2" fontWeight={700} color="success.main" sx={{ fontFamily: FONT }}>
                ${(parseFloat(formData.suggested_amounts[0]) - parseFloat(calculateProcessingFee(formData.suggested_amounts[0]))).toFixed(2)}
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      )}
    </Card>
  );

  const renderStepSettings = () => (
    <Stack spacing={3}>
      {/* Customer Info & Thank You */}
      <Card elevation={0} sx={sectionCardSx(theme)}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <SettingsIcon sx={{ color: theme.palette.primary.main }} fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: FONT }}>
            General Settings
          </Typography>
        </Stack>

        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={formData.collect_customer_info}
                  onChange={handleInputChange}
                  name="collect_customer_info"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontFamily: FONT }}>
                  Collect {formData.payment_type === 'donation' ? 'Donor' : 'Customer'} Information (Name & Email)
                </Typography>
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Thank You Message"
              name="thank_you_message"
              value={formData.thank_you_message}
              onChange={handleInputChange}
              size="small"
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Redirect URL (Optional)"
              name="redirect_url"
              value={formData.redirect_url}
              onChange={handleInputChange}
              placeholder="https://yoursite.com/thank-you"
              size="small"
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Expires At (Optional)"
              name="expires_at"
              value={formData.expires_at}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={fieldSx}
            />
          </Grid>
        </Grid>
      </Card>

      {/* Chatbot Integration */}
      <Card elevation={0} sx={sectionCardSx(theme)}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <SettingsIcon sx={{ color: theme.palette.primary.main }} fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: FONT }}>
            Chatbot Integration
          </Typography>
        </Stack>

        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.info.main, 0.04),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={formData.enable_for_chatbot}
                onChange={handleInputChange}
                name="enable_for_chatbot"
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: FONT }}>
                  Enable for Chatbot Integration
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontFamily: FONT }}>
                  Allow this payment link to be used within chatbot conversations. This setting is not visible to customers.
                </Typography>
              </Box>
            }
            sx={{
              m: 0,
              alignItems: 'flex-start',
              '& .MuiFormControlLabel-label': { ml: 1 }
            }}
          />
        </Paper>

        {formData.enable_for_chatbot && (
          <Alert
            severity="success"
            sx={{ mt: 2, borderRadius: 2 }}
            icon={<InfoOutlinedIcon fontSize="small" />}
          >
            <Typography variant="body2" sx={{ fontFamily: FONT }}>
              This payment link will be available for use in your chatbot. Customers interacting with your chatbot will be able to access this payment option.
            </Typography>
          </Alert>
        )}
      </Card>
    </Stack>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0: return renderStepDetails();
      case 1: return renderStepAmounts();
      case 2: return renderStepPaymentMethods();
      case 3: return renderStepSettings();
      default: return null;
    }
  };

  // =========================================================================
  //  MAIN RENDER
  // =========================================================================
  return (
    <Box>
      {/* Stepper */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          p: 3,
          mb: 3
        }}
      >
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    fontFamily: FONT,
                    '&.Mui-active': { color: theme.palette.primary.main },
                    '&.Mui-completed': { color: theme.palette.success.main }
                  }
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: FONT,
                    fontWeight: activeStep === index ? 700 : 500,
                    color: activeStep === index ? 'text.primary' : 'text.secondary'
                  }}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Card>

      {/* Step Content */}
      <Box sx={{ mb: 3 }}>
        {renderStepContent(activeStep)}
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontFamily: FONT }}>{error}</Typography>
        </Alert>
      )}

      {/* Navigation Buttons */}
      {permission.canAdd && (
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            p: 3
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems="center">
            {/* Left side: Back + Preview */}
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                disabled={activeStep === 0}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontFamily: FONT,
                  px: 3
                }}
              >
                Back
              </Button>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={() => setShowPreview(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontFamily: FONT,
                  px: 3,
                  color: 'text.secondary',
                  borderColor: theme.palette.divider,
                  '&:hover': {
                    borderColor: theme.palette.text.secondary,
                    bgcolor: alpha(theme.palette.text.secondary, 0.04)
                  }
                }}
              >
                Preview
              </Button>
            </Stack>

            {/* Right side: Next / Submit */}
            {activeStep < STEPS.length - 1 ? (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={handleNext}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontFamily: FONT,
                  px: 4,
                  py: 1.2,
                  minWidth: 160
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontFamily: FONT,
                  px: 4,
                  py: 1.2,
                  minWidth: 200,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                  }
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={22} sx={{ color: 'white' }} />
                ) : (
                  'Create Payment Link'
                )}
              </Button>
            )}
          </Stack>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ fontFamily: FONT }}>
            Payment Link Preview
          </Typography>
        </DialogTitle>
        <DialogContent>
          <PaymentLinkPreview formData={formData} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowPreview(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontFamily: FONT
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Preview Component
const PaymentLinkPreview = ({ formData }) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        mt: 2,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          sx={{ fontWeight: 700, fontFamily: FONT }}
          color="text.primary"
        >
          {formData.title || 'Payment Title'}
        </Typography>

        {formData.description && (
          <Typography variant="body2" sx={{ mb: 3, fontFamily: FONT }} color="text.secondary">
            {formData.description}
          </Typography>
        )}

        {formData.purposes && formData.purposes.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, fontFamily: FONT }} color="text.primary">
              Select Purpose:
            </Typography>
            <FormControl fullWidth size="small" sx={{'& .MuiOutlinedInput-root': { borderRadius: 2 }}}>
              <InputLabel>Purpose</InputLabel>
              <Select
                label="Purpose"
                defaultValue={formData.purposes[0]}
                sx={{ borderRadius: 2 }}
              >
                {formData.purposes.map((purpose, index) => (
                  <MenuItem key={index} value={purpose}>
                    {purpose}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontFamily: FONT }} color="text.primary">
          Select Amount:
        </Typography>

        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {formData.suggested_amounts.map((amount, index) => (
            amount && (
              <Grid item xs={6} sm={3} key={index}>
                <Chip
                  label={`$${amount}`}
                  variant="outlined"
                  clickable
                  sx={{
                    width: '100%',
                    height: 36,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    fontFamily: FONT,
                    borderRadius: 2
                  }}
                />
              </Grid>
            )
          ))}
        </Grid>

        {formData.custom_amount_enabled && (
          <TextField
            fullWidth
            type="number"
            label="Custom Amount"
            placeholder="Enter amount"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            size="small"
          />
        )}

        {formData.collect_customer_info && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontFamily: FONT }} color="text.primary">
              {formData.payment_type === 'donation' ? 'Donor' : 'Customer'} Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Name" size="small" sx={{'& .MuiOutlinedInput-root': { borderRadius: 2 }}} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" type="email" size="small" sx={{'& .MuiOutlinedInput-root': { borderRadius: 2 }}} />
              </Grid>
            </Grid>
          </Box>
        )}

        {formData.disclaimer && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2" sx={{ fontFamily: FONT }}>{formData.disclaimer}</Typography>
          </Alert>
        )}

        <Button
          variant="contained"
          fullWidth
          size="large"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontFamily: FONT,
            py: 1.3
          }}
        >
          {formData.payment_type === 'donation' ? 'Donate Now' : 'Pay Now'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default CreatePaymentLinkForm;
